import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalShell, ModalFoot, ModalHead } from './MergeModal';
import { listProducts, type Product } from '../api/products';
import { createManualOrder, type ManualOrderPayload } from '../api/orders';
import type { ShippingMethod, ShirtColor, ShirtCut, Size } from '../types';
import { formatGroszeAsZl } from '../util/money';
import { PaczkomatPicker } from '../components/PaczkomatPicker';
import { PhoneInput } from '../components/PhoneInput';
import type { SelectedPaczkomat } from '../util/paczkomat';
import { SHIPPING_COUNTRIES } from '../util/shippingCountries';

const METHODS: { id: ShippingMethod; label: string }[] = [
  { id: 'paczkomat', label: 'Paczkomat' },
  { id: 'kurier', label: 'Kurier' },
  { id: 'odbior', label: 'Odbiór osobisty' },
  { id: 'zagranica', label: 'Zagranica' },
];

const ALL_SIZES: Size[] = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];

// Loose on purpose: the API validates the address, this only decides whether the link checkbox
// is worth offering.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ManualItem = {
  id: string;
  color: ShirtColor | '';
  cut: ShirtCut | '';
  size: Size | '';
  qty: number;
};

function newRow(): ManualItem {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  const id =
    c && typeof c.randomUUID === 'function'
      ? c.randomUUID()
      : `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  return { id, color: '', cut: '', size: '', qty: 1 };
}

type Props = {
  /** Called once the order exists, whether or not its payment link went out. */
  onCreated?: () => void | Promise<void>;
};

export function ManualAddModal({ onCreated }: Props) {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneValid, setPhoneValid] = useState(false);
  const [email, setEmail] = useState('');
  const [sendLink, setSendLink] = useState(false);
  const [ticket, setTicket] = useState('');
  const [method, setMethod] = useState<ShippingMethod | ''>('');
  const [paczkomat, setPaczkomat] = useState<SelectedPaczkomat | null>(null);
  const [country, setCountry] = useState('');
  const [note, setNote] = useState('');
  const [items, setItems] = useState<ManualItem[]>([newRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unsentLink, setUnsentLink] = useState<{ id: string; error: string } | null>(null);

  useEffect(() => {
    listProducts()
      .then((r) => setProducts(r.products))
      .catch(() => setProducts([]));
  }, []);

  const close = () => navigate('/admin/zamowienia');

  const productByColor = useMemo(() => {
    const m = new Map<ShirtColor, Product>();
    for (const p of products) m.set(p.color, p);
    return m;
  }, [products]);

  const total = items.reduce((s, it) => {
    const p = it.color ? productByColor.get(it.color) : undefined;
    return s + (p ? p.price_grosze * Math.max(0, it.qty) : 0);
  }, 0);

  const updateItem = (id: string, patch: Partial<ManualItem>) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const addItem = () => setItems((prev) => [...prev, newRow()]);
  const removeItem = (id: string) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const paczkomatRequired = method === 'paczkomat';
  const countryRequired = method === 'zagranica';
  const trimmedEmail = email.trim();
  const emailLooksValid = EMAIL_SHAPE.test(trimmedEmail);
  const willSendLink = sendLink && emailLooksValid;
  const canSubmit =
    !submitting &&
    products.length > 0 &&
    name.trim().length > 0 &&
    phoneValid &&
    !!method &&
    (!paczkomatRequired || !!paczkomat) &&
    (!countryRequired || !!country) &&
    items.length > 0 &&
    items.every((it) => it.color && it.cut && it.size && it.qty > 0 && productByColor.has(it.color as ShirtColor));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: ManualOrderPayload = {
        customerName: name.trim(),
        email: trimmedEmail || undefined,
        phone,
        items: items.map((it) => ({
          product_id: productByColor.get(it.color as ShirtColor)!.id,
          cut: it.cut as ShirtCut,
          size: it.size as Size,
          qty: it.qty,
        })),
        shipping_method: method as ShippingMethod,
        shipping_address: countryRequired
          ? { international: { country } }
          : paczkomat
            ? { paczkomat }
            : {},
        internal_note: note.trim() || (ticket.trim() ? `Bilet ${ticket.trim()}` : 'Wpis ręczny'),
        // Cash is reconciled afterwards with "Oznacz jako opłacone", so every manual order can
        // still be sent a payment link later.
        initial_status: 'awaiting_payment',
        send_payment_link: willSendLink,
      };
      const created = await createManualOrder(payload);
      await onCreated?.();
      if (created.paymentLinkEmail && !created.paymentLinkEmail.ok) {
        setUnsentLink({ id: created.id, error: created.paymentLinkEmail.error });
        return;
      }
      close();
    } catch (err) {
      setError(manualErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (unsentLink) {
    return (
      <ModalShell onClose={close} width={520}>
        <ModalHead title="Zamówienie utworzone" subtitle="OPERACJA · RĘCZNIE" onClose={close} />
        <div style={{ padding: 22 }}>
          <div role="alert" style={{ fontSize: 13, lineHeight: 1.5 }}>
            Zamówienie <b className="mono">{unsentLink.id}</b> zostało utworzone, ale maila z linkiem do
            płatności nie udało się wysłać <span className="mono">({unsentLink.error})</span>. Otwórz
            zamówienie i wyślij link ponownie albo skopiuj go i przekaż klientowi.
          </div>
        </div>
        <ModalFoot>
          <button className="btn-a" onClick={close}>
            Zamknij
          </button>
          <button
            className="btn-a primary"
            onClick={() => navigate(`/admin/zamowienia/${encodeURIComponent(unsentLink.id)}`)}
          >
            Przejdź do zamówienia
          </button>
        </ModalFoot>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={close} width={820}>
      <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--admin-line)' }}>
        <div className="mono" style={{ fontSize: 11, color: 'var(--admin-muted)', letterSpacing: '.1em' }}>
          OPERACJA · RĘCZNIE
        </div>
        <h1 style={{ marginTop: 4 }}>Nowe zamówienie</h1>
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--admin-muted)' }}>
          Trafi do „Czekają na płatność”. Gotówkę rozliczysz przyciskiem „Oznacz jako opłacone”.
        </div>
      </div>

      <div className="manual-grid">
        <div>
          <Field label="Imię i nazwisko *" htmlFor="manual-name">
            <input
              id="manual-name"
              className="input-a"
              style={{ width: '100%' }}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label="Telefon *">
            <PhoneInput
              variant="admin"
              value={phone}
              onChange={(next, valid) => {
                setPhone(next);
                setPhoneValid(valid);
              }}
            />
          </Field>
          <Field label="Email" htmlFor="manual-email">
            <input
              id="manual-email"
              type="email"
              className="input-a"
              style={{ width: '100%' }}
              placeholder="opcjonalny"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {trimmedEmail && (
              <label
                style={{
                  // Overrides the uppercase display face `.field label` gives field captions.
                  fontFamily: 'var(--body)',
                  textTransform: 'none',
                  letterSpacing: 'normal',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  margin: '6px 0 0',
                  fontSize: 12,
                  color: emailLooksValid ? 'var(--admin-ink)' : 'var(--admin-muted)',
                  cursor: emailLooksValid ? 'pointer' : 'not-allowed',
                }}
              >
                <input
                  type="checkbox"
                  checked={willSendLink}
                  disabled={!emailLooksValid}
                  onChange={(e) => setSendLink(e.target.checked)}
                />
                Wyślij link do płatności
              </label>
            )}
          </Field>
          <Field label={<>Numer biletu <span style={{ color: 'var(--admin-muted)' }}>(opcjonalnie)</span></>}>
            <input
              className="input-a mono"
              style={{ width: '100%' }}
              placeholder="TKT-2026-…"
              value={ticket}
              onChange={(e) => setTicket(e.target.value)}
            />
          </Field>
        </div>

        <div>
          <Field label="Sposób wysyłki *">
            <div className="manual-methods">
              {METHODS.map((m) => {
                const on = method === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setMethod(m.id)}
                    className="btn-a"
                    style={{
                      fontSize: 12,
                      background: on ? 'var(--ink)' : 'var(--admin-card)',
                      color: on ? 'var(--paper)' : 'var(--admin-ink)',
                      borderColor: on ? 'var(--ink)' : 'var(--admin-line-strong)',
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </Field>

          {method === 'paczkomat' && (
            <Field label="Paczkomat *">
              <PaczkomatPicker
                theme="admin"
                value={paczkomat}
                onChange={setPaczkomat}
              />
            </Field>
          )}

          {method === 'zagranica' && (
            <Field label="Kraj docelowy *">
              <select
                className="input-a"
                style={{ width: '100%' }}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                <option value="">— wybierz kraj —</option>
                {SHIPPING_COUNTRIES.map((c) => (
                  <option key={c.iso} value={c.iso}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Notatka wewnętrzna">
            <textarea
              className="input-a"
              rows={2}
              style={{ width: '100%', resize: 'none' }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <h2>Pozycje</h2>
          <table className="manual-items">
            <thead>
              <tr>
                <th>Kolor</th>
                <th>Krój</th>
                <th>Rozmiar</th>
                <th>Ilość</th>
                <th style={{ textAlign: 'right' }}>Cena jedn.</th>
                <th style={{ textAlign: 'right' }}>Suma</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const product = row.color ? productByColor.get(row.color) : undefined;
                const lineTotal = product ? product.price_grosze * row.qty : 0;
                return (
                  <tr key={row.id}>
                    <td className="c-color" data-label="Kolor">
                      <select
                        className="input-a"
                        value={row.color}
                        onChange={(e) => updateItem(row.id, { color: e.target.value as ShirtColor | '' })}
                      >
                        <option value="">— wybierz —</option>
                        {products.map((p) => (
                          <option key={p.color} value={p.color}>
                            {p.color}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="c-cut" data-label="Krój">
                      <select
                        className="input-a"
                        value={row.cut}
                        onChange={(e) => updateItem(row.id, { cut: e.target.value as ShirtCut | '' })}
                      >
                        <option value="">— wybierz —</option>
                        <option value="męski">męski</option>
                        <option value="damski">damski</option>
                      </select>
                    </td>
                    <td className="c-size" data-label="Rozmiar">
                      <select
                        className="input-a"
                        value={row.size}
                        onChange={(e) => updateItem(row.id, { size: e.target.value as Size | '' })}
                      >
                        <option value="">— wybierz —</option>
                        {ALL_SIZES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="c-qty" data-label="Ilość">
                      <input
                        className="input-a mono"
                        inputMode="numeric"
                        style={{ width: 60 }}
                        value={row.qty}
                        onChange={(e) => updateItem(row.id, { qty: Math.max(1, Number(e.target.value) || 1) })}
                      />
                    </td>
                    <td className="mono c-unit" style={{ textAlign: 'right' }}>
                      {product ? formatGroszeAsZl(product.price_grosze) : '—'}
                    </td>
                    <td className="mono c-total" data-label="Suma" style={{ textAlign: 'right', fontWeight: 700 }}>
                      {product ? formatGroszeAsZl(lineTotal) : '—'}
                    </td>
                    <td className="c-remove" style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn-a"
                        onClick={() => removeItem(row.id)}
                        disabled={items.length <= 1}
                        title="Usuń pozycję"
                        style={{
                          padding: '4px 10px',
                          fontSize: 14,
                          opacity: items.length <= 1 ? 0.4 : 1,
                          cursor: items.length <= 1 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn-a" onClick={addItem}>
              + Dodaj kolejną pozycję
            </button>
          </div>

          {error && (
            <div
              className="mono"
              style={{
                marginTop: 12,
                padding: 10,
                border: '1px solid var(--admin-danger, #c04)',
                color: 'var(--admin-danger, #c04)',
                fontSize: 12,
              }}
            >
              Błąd: {error}
            </div>
          )}
        </div>
      </div>

      <ModalFoot
        left={
          <>
            <span className="mono" style={{ fontSize: 11, color: 'var(--admin-muted)', letterSpacing: '.1em' }}>
              SUMA
            </span>
            <span className="display" style={{ fontSize: 22, marginLeft: 10, color: 'var(--orange-deep)' }}>
              {formatGroszeAsZl(total).toUpperCase()}
            </span>
          </>
        }
      >
        <button className="btn-a" onClick={close}>
          Anuluj
        </button>
        <button className="btn-a primary" onClick={submit} disabled={!canSubmit}>
          {willSendLink ? '+ Utwórz i wyślij link' : '+ Utwórz zamówienie'}
        </button>
      </ModalFoot>
    </ModalShell>
  );
}

function manualErrorMessage(err: unknown): string {
  const body = (err as { body?: { error?: string } }).body;
  switch (body?.error) {
    case 'payment_link_requires_email':
      return 'Link do płatności wymaga poprawnego adresu email.';
    case 'stripe_unavailable':
      return 'Płatności online są wyłączone · odznacz „Wyślij link do płatności”.';
    default:
      return err instanceof Error ? err.message : 'submit_failed';
  }
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: import('react').ReactNode;
  htmlFor?: string;
  children: import('react').ReactNode;
}) {
  return (
    <div className="field" style={{ marginBottom: 12 }}>
      <label
        htmlFor={htmlFor}
        style={{ color: 'var(--admin-ink)', fontSize: 11, fontFamily: 'var(--display)', display: 'block', marginBottom: 4 }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
